import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Input,
  Box,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export type fileUploadType = {
  open: boolean;
  onFileUploadClose: () => void;
  onFileUploadSubmit: (file: File) => Promise<{
    success: boolean;
    message: string;
    data?: any;
  }>;
  acceptedFileTypes?: string;
};

const FileUploadModal: React.FC<fileUploadType> = ({ open, onFileUploadClose, onFileUploadSubmit, acceptedFileTypes }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleFileUploadSubmitAction = async () => {
    if (selectedFile) {
      setIsLoading(true);
      try {
        const result = await onFileUploadSubmit(selectedFile);
        setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
        if (result.success) {
          setSelectedFile(null);
          onFileUploadClose();
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to upload file', severity: 'error' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onFileUploadClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#1976d2',
            color: 'white',
            py: 2,
            px: 3,
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Upload Settlement Prices
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onFileUploadClose}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box
            sx={{
              border: '2px dashed #90caf9',
              borderRadius: 2,
              p: 3,
              mb: 3,
              textAlign: 'center',
              bgcolor: '#f5faff',
              transition: 'border-color 0.3s ease',
              '&:hover': {
                borderColor: '#1976d2',
              },
            }}
          >
            <Input
              type="file"
              onChange={handleFileUploadChange}
              fullWidth
              disableUnderline
              sx={{ display: 'none' }}
              id="file-upload-input"
              inputProps={{ accept: acceptedFileTypes }}
            />
            <label htmlFor="file-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#1976d2',
                    color: 'white',
                    borderColor: '#1976d2',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                Choose File
              </Button>
            </label>
            {selectedFile && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedFile.name}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onFileUploadClose}
            color="secondary"
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              px: 3,
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#f5f5f5',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFileUploadSubmitAction}
            color="primary"
            variant="contained"
            disabled={!selectedFile || isLoading}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              px: 3,
              borderRadius: 2,
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease',
              },
              '&.Mui-disabled': {
                bgcolor: '#b0bec5',
                color: '#e0e0e0',
              },
            }}
          >
            {isLoading ? 'Uploading...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileUploadModal;